import React, { useState } from "react";
import { Crown, Plus, Star, Trash2 } from "lucide-react";
import { Affix, Btn, Card, CardBody, CardHeader, Field, Input } from "../components/primitives";

const COLORS = ["#94A3B8", "#B7791F", "#475569", "#7C3AED", "#15803C", "#1E63D6"];

const slug = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || `tier-${Math.random().toString(36).slice(2, 7)}`;

// Tier data lives in TWO buckets post senior-review reshape:
//   - earn_config.tiers[]   identity + spend_from + multiplier + perks
//   - redeem_config.tiers[] same id + redeem_cap_pct
// joinTiers stitches them into the flat shape the UI edits; splitOnSave
// fans the edit back out into the two arrays in lockstep so the backend
// (Joi: tier-id parity required) doesn't reject the write.
const joinTiers = (config) => {
  const earn = config?.earn_config?.tiers || [];
  const redeem = config?.redeem_config?.tiers || [];
  const capById = new Map(redeem.map((r) => [r.id, r.redeem_cap_pct]));
  return earn.map((e) => ({
    ...e,
    redeem_cap_pct: capById.has(e.id) ? capById.get(e.id) : 30,
  }));
};

// Inverse of joinTiers — flat row → {earn, redeem} pair so we can splice
// back into config.earn_config.tiers + config.redeem_config.tiers.
const splitRow = (row) => {
  const { redeem_cap_pct, ...earnFields } = row;
  return {
    earn: earnFields,
    redeem: { id: row.id, redeem_cap_pct },
  };
};

const TiersTab = ({ config, setConfig, readOnly }) => {
  const tiers = joinTiers(config);
  const [activeIdx, setActiveIdx] = useState(0);

  const writeBack = (nextFlatTiers) => {
    const split = nextFlatTiers.map(splitRow);
    setConfig({
      ...config,
      earn_config: {
        ...(config.earn_config || {}),
        tiers: split.map((s) => s.earn),
      },
      redeem_config: {
        ...(config.redeem_config || {}),
        tiers: split.map((s) => s.redeem),
      },
    });
  };

  const setTier = (idx, patch) => {
    const next = tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    writeBack(next);
  };

  const addTier = () => {
    if (readOnly) return;
    const newTier = {
      id: slug(`tier ${tiers.length + 1}`),
      name: `Tier ${tiers.length + 1}`,
      color: COLORS[tiers.length % COLORS.length],
      spend_from: 0,
      multiplier: 1,
      redeem_cap_pct: 30,
      perks: [],
    };
    writeBack([...tiers, newTier]);
    setActiveIdx(tiers.length);
  };

  const removeTier = (idx) => {
    if (readOnly) return;
    if (!window.confirm("Remove this tier? Members on it will need to be re-evaluated.")) return;
    const next = tiers.filter((_, i) => i !== idx);
    writeBack(next);
    if (activeIdx >= next.length) setActiveIdx(Math.max(0, next.length - 1));
  };

  const active = tiers[activeIdx];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Membership Tiers</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[700px]">
            Reward higher spenders with point multipliers, larger redemption caps and exclusive perks. Evaluated on rolling 12-month spend.
          </p>
        </div>
        <Btn variant="primary" onClick={addTier} disabled={readOnly}>
          <Plus size={15} />
          Add tier
        </Btn>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {tiers.map((t, i) => (
          <button
            key={t.id + i}
            onClick={() => setActiveIdx(i)}
            className={`rounded-xl border text-left bg-white overflow-hidden transition ${
              activeIdx === i ? "border-primary shadow-[0_0_0_3px_rgba(39,125,254,0.1)]" : "border-[#E6E9EF]"
            }`}
          >
            <div style={{ height: 6, background: t.color }} />
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Crown size={18} style={{ color: t.color }} />
                  <span className="font-bold text-[14px]">{t.name}</span>
                </span>
                <span
                  className="px-2 py-0.5 rounded-full text-[11px] font-bold"
                  style={{ background: t.color + "1a", color: t.color }}
                >
                  {t.multiplier}× pts
                </span>
              </div>
              <div className="text-[11px] text-[#64748B] mt-3">Qualifying spend (from)</div>
              <div className="text-[15px] font-semibold mt-0.5">
                ₹{Number(t.spend_from || 0).toLocaleString("en-IN")}
              </div>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <Card>
          <CardHeader
            icon={<Crown size={18} />}
            tone="gold"
            title={`${active.name} tier settings`}
            desc="Editing the benefits for this level"
            action={
              <Btn variant="ghost" size="sm" onClick={() => removeTier(activeIdx)} disabled={readOnly}>
                <Trash2 size={14} />
                Remove
              </Btn>
            }
          />
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Name" required>
                <Input
                  value={active.name}
                  onChange={(e) =>
                    setTier(activeIdx, {
                      name: e.target.value,
                      id: active.id || slug(e.target.value),
                    })
                  }
                  disabled={readOnly}
                />
              </Field>
              <Field label="Slug (internal id)" required>
                <Input
                  value={active.id}
                  onChange={(e) => setTier(activeIdx, { id: slug(e.target.value) })}
                  disabled={readOnly}
                  mono
                />
              </Field>
              <Field label="Colour">
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => !readOnly && setTier(activeIdx, { color: c })}
                      className={`w-8 h-8 rounded-md border-2 transition ${
                        active.color === c ? "border-[#0F172A]" : "border-transparent"
                      }`}
                      style={{ background: c }}
                      disabled={readOnly}
                    />
                  ))}
                </div>
              </Field>
              <Field label="Qualifying spend (from)" help="Rolling 12-month spend.">
                <Affix pre="₹">
                  <Input
                    type="number"
                    value={active.spend_from}
                    onChange={(e) =>
                      setTier(activeIdx, { spend_from: Number(e.target.value) })
                    }
                    style={{ paddingLeft: 28 }}
                    disabled={readOnly}
                  />
                </Affix>
              </Field>
              <Field label="Points multiplier">
                <Affix post="×">
                  <Input
                    type="number"
                    step="0.1"
                    value={active.multiplier}
                    onChange={(e) =>
                      setTier(activeIdx, { multiplier: Number(e.target.value) })
                    }
                    disabled={readOnly}
                  />
                </Affix>
              </Field>
              <Field label="Max redemption / bill">
                <Affix post="%">
                  <Input
                    type="number"
                    value={active.redeem_cap_pct}
                    onChange={(e) =>
                      setTier(activeIdx, { redeem_cap_pct: Number(e.target.value) })
                    }
                    disabled={readOnly}
                  />
                </Affix>
              </Field>
            </div>

            <div className="text-[11px] font-bold tracking-wider uppercase text-[#64748B] mt-6 mb-2">
              Perks
            </div>
            <div className="flex flex-wrap gap-2">
              {(active.perks || []).map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] bg-[#F1F5F9] text-[#475569]"
                >
                  <Star size={12} />
                  {p}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() =>
                        setTier(activeIdx, {
                          perks: active.perks.filter((_, j) => j !== i),
                        })
                      }
                      className="ml-1 text-[#94A3B8] hover:text-[#E11D48]"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => {
                    const v = window.prompt("Perk text? (max 120 chars)");
                    if (v && v.trim()) {
                      const text = v.trim().slice(0, 120);
                      setTier(activeIdx, { perks: [...(active.perks || []), text] });
                    }
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] border border-dashed border-[#D9DEE6] text-[#64748B] hover:text-primary hover:border-primary"
                >
                  <Plus size={12} />
                  Add perk
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default TiersTab;
